import { useId, type ReactNode } from 'react';

/** Form primitives. Every field in the wizard is built from these, so labelling,
 *  spacing, focus and error treatment stay identical across eight steps. */

const CONTROL =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 ' +
  'placeholder:text-slate-500 dark:placeholder:text-slate-400 transition-colors hover:border-slate-400 ' +
  'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500';

const ERROR_RING = 'border-red-400 dark:border-red-500';

/** The hint and the error are read out with the field, not just drawn under
 *  it. The asterisk is decoration; `aria-required` is what gets announced. */
function describedBy(id: string, hint: unknown, error: unknown): string | undefined {
  const ids = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean);
  return ids.length > 0 ? ids.join(' ') : undefined;
}

function Wrapper({
  id,
  label,
  hint,
  error,
  required,
  children,
}: {
  id: string;
  label: string;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-800 dark:text-slate-200">
        {label}
        {required && (
          <span className="ml-1 text-copper-600 dark:text-copper-400" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {hint && (
        <p id={`${id}-hint`} className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      )}
      <div className="mt-1.5">{children}</div>
      {error && (
        <p id={`${id}-error`} className="mt-1.5 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function TextField({
  label,
  value,
  onChange,
  hint,
  error,
  required,
  placeholder,
  type = 'text',
  disabled,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  placeholder?: string;
  type?: 'text' | 'date' | 'tel' | 'email';
  disabled?: boolean;
  maxLength?: number;
}) {
  const id = useId();
  return (
    <Wrapper id={id} label={label} hint={hint} error={error} required={required}>
      <input
        id={id}
        type={type}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        onChange={(event) => onChange(event.target.value)}
        className={`${CONTROL} ${error ? ERROR_RING : ''}`}
      />
    </Wrapper>
  );
}

export function TextArea({
  label,
  value,
  onChange,
  hint,
  rows = 4,
  placeholder,
  disabled,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  hint?: ReactNode;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
}) {
  const id = useId();
  return (
    <Wrapper id={id} label={label} hint={hint}>
      <textarea
        id={id}
        rows={rows}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-describedby={describedBy(id, hint, undefined)}
        onChange={(event) => onChange(event.target.value)}
        className={CONTROL}
      />
    </Wrapper>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  hint,
  error,
  required,
  placeholder = '-',
  disabled,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: { value: string; label: string }[];
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <Wrapper id={id} label={label} hint={hint} error={error} required={required}>
      <select
        id={id}
        value={value}
        disabled={disabled}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        onChange={(event) => onChange(event.target.value)}
        className={`${CONTROL} ${error ? ERROR_RING : ''}`}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Wrapper>
  );
}

export function RadioGroup<T extends string>({
  label,
  value,
  onChange,
  options,
  error,
  required,
  hint,
}: {
  label: string;
  value: T | null;
  onChange: (next: T) => void;
  options: { value: T; label: string }[];
  error?: string;
  required?: boolean;
  hint?: ReactNode;
}) {
  const id = useId();
  return (
    <fieldset aria-describedby={describedBy(id, hint, error)}>
      <legend className="text-sm font-medium text-slate-800 dark:text-slate-200">
        {label}
        {required && (
          <span className="ml-1 text-copper-600 dark:text-copper-400" aria-hidden="true">
            *
          </span>
        )}
        {required && <span className="sr-only">(required)</span>}
      </legend>
      {hint && (
        <p id={`${id}-hint`} className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((option) => {
          const active = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(option.value)}
              className={
                active
                  ? 'rounded-lg border border-brand-600 bg-brand-50 px-3.5 py-2 text-sm font-medium text-brand-800 dark:border-brand-500 dark:bg-brand-900/50 dark:text-brand-100'
                  : 'rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm text-slate-700 transition-colors hover:border-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
              }
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {error && (
        <p id={`${id}-error`} className="mt-1.5 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}

/** A section heading inside a step, for grouping without another card. */
export function FieldGroup({ title, description, children }: { title?: string; description?: string; children: ReactNode }) {
  return (
    <div>
      {title && (
        <h3 className="text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">{title}</h3>
      )}
      {description && <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{description}</p>}
      <div className={title || description ? 'mt-4' : ''}>{children}</div>
    </div>
  );
}
